import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, IconButton, Box, CircularProgress, Typography, Alert } from '@mui/material';
import { Close } from '@mui/icons-material';
import axiosInstance from '@/setup/axios/axiosInstance';
import OfficeFileViewer from './OfficeFileViewer';

interface UniversalFileViewerProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  documentName: string;
}

const UniversalFileViewer: React.FC<UniversalFileViewerProps> = ({ 
  open, 
  onClose, 
  documentId, 
  documentName 
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string>('');

  useEffect(() => {
    if (!open || !documentId) return;

    let blobUrl: string | null = null;

    const loadDocument = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get document URL from backend
        const response = await axiosInstance.get(`documents/${documentId}/download/`, {
          responseType: 'blob'
        });

        // Create blob URL for the document
        const blob = response.data;
        
        // Validate blob
        if (!blob || blob.size === 0) {
          throw new Error('File không tồn tại hoặc rỗng');
        }
        
        blobUrl = URL.createObjectURL(blob);
        setFileUrl(blobUrl);

        // Determine file type
        const extension = documentName.split('.').pop()?.toLowerCase() || '';
        setFileType(extension);

      } catch (err) {
        console.error('Failed to load document:', err);
        setError('Không thể tải tài liệu. Vui lòng thử lại sau.');
      } finally {
        setIsLoading(false);
      }
    };

    loadDocument();
    
    // Cleanup function
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [open, documentId, documentName]);

  const handleClose = () => {
    if (fileUrl) {
      URL.revokeObjectURL(fileUrl);
    }
    setFileUrl(null);
    setFileType('');
    onClose();
  };

  const renderFileContent = () => {
    if (!fileUrl) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 4,
            textAlign: 'center'
          }}
        >
          <Typography variant="h6" color="text.secondary">
            Không thể tải file
          </Typography>
        </Box>
      );
    }

    // PDF files
    if (fileType === 'pdf') {
      return (
        <iframe
          src={fileUrl}
          style={{
            width: '100%',
            height: '100%',
            border: 'none'
          }}
          title={documentName}
          onError={() => setError('Không thể hiển thị file PDF')}
        />
      );
    }

    // Image files
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(fileType)) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            p: 2
          }}
        >
          <img
            src={fileUrl}
            alt={documentName}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain'
            }}
            onError={() => setError('Không thể hiển thị hình ảnh')}
          />
        </Box>
      );
    }

    // Office files - use multiple fallback options
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileType)) {
      return (
        <Box sx={{ height: '100%', position: 'relative' }}>
          <OfficeFileViewer 
            fileUrl={fileUrl} 
            fileName={documentName}
            fileType={fileType}
            onError={() => setError('Không thể preview file Office. Vui lòng tải xuống để xem.')}
          />
        </Box>
      );
    }

    // Other files
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 4,
          textAlign: 'center'
        }}
      >
        <Alert severity="warning" sx={{ mb: 2 }}>
          Định dạng file này không thể preview. Vui lòng tải xuống để xem.
        </Alert>
        <Typography variant="h6" gutterBottom>
          {documentName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Định dạng: {fileType.toUpperCase()}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div">
          {documentName}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 1,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Đang tải tài liệu...
            </Typography>
          </Box>
        )}
        
        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 1,
            }}
          >
            <Typography variant="h6" color="error">
              {error}
            </Typography>
          </Box>
        )}
        
        <Box
          ref={containerRef}
          sx={{
            height: '100%',
            width: '100%',
            minHeight: '500px',
          }}
        >
          {renderFileContent()}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UniversalFileViewer;
